import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import RefreshIcon from '@mui/icons-material/Refresh';

const MAX_CONCURRENT_UPLOADS = 4;

const FedexFiles = () => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]); // enforced single item: [{file, id, progress, status, error}]
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadResponses, setUploadResponses] = useState([]); // array of displayable objects
  const [isRecalling, setIsRecalling] = useState(false);

  // Load any existing response from this session
  useEffect(() => {
    try {
      // New array-based key
      const listRaw = sessionStorage.getItem('fedexUploadResponses');
      if (listRaw) {
        const parsed = JSON.parse(listRaw);
        if (Array.isArray(parsed)) {
          setUploadResponses(parsed);
          return;
        }
      }
      // Backward compatibility: migrate previous single response if present
      const savedSingle = sessionStorage.getItem('fedexUploadResponse');
      if (savedSingle) {
        const single = JSON.parse(savedSingle);
        const normalized = normalizeResponseToDisplayObject(single);
        const arr = [normalized];
        setUploadResponses(arr);
        sessionStorage.setItem('fedexUploadResponses', JSON.stringify(arr));
      }
    } catch {}
  }, []);

  // Normalize any API response to a flat displayable object (one row)
  const normalizeResponseToDisplayObject = useCallback((r) => {
    if (!r) return {};
    const candidate = (r && typeof r === 'object' && !Array.isArray(r))
      ? (r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : r)
      : (Array.isArray(r) && r.length > 0 && typeof r[0] === 'object' && !Array.isArray(r[0]) ? r[0] : { value: typeof r === 'string' ? r : JSON.stringify(r) });
    return candidate || {};
  }, []);

  const onFilesSelected = useCallback((fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (arr.length > 1) {
      setSnackbar({ open: true, message: 'Only one file per upload is allowed', severity: 'warning' });
    }
    const f = arr[0];
    const picked = [{
      file: f,
      id: `${Date.now()}_0_${f.name}`,
      progress: 0,
      status: 'pending',
      error: ''
    }];
    setFiles(picked); // replace any existing selection
  }, []);

  const handleBrowseClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback((e) => {
    onFilesSelected(e.target.files);
    e.target.value = '';
  }, [onFilesSelected]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length) {
      onFilesSelected(dt.files);
    }
  }, [onFilesSelected]);

  const totalSize = useMemo(() => files.reduce((acc, f) => acc + (f.file?.size || 0), 0), [files]);
  const humanSize = useMemo(() => {
    const s = totalSize;
    if (s > 1024 * 1024 * 1024) return `${(s / (1024*1024*1024)).toFixed(2)} GB`;
    if (s > 1024 * 1024) return `${(s / (1024*1024)).toFixed(2)} MB`;
    if (s > 1024) return `${(s / 1024).toFixed(2)} KB`;
    return `${s} B`;
  }, [totalSize]);

  const removeFile = useCallback((id) => {
    setFiles((prev) => prev.filter(f => f.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setFiles([]);
  }, []);

  const uploadQueue = useRef([]);
  const activeCount = useRef(0);

  const updateItem = (id, patch) => {
    setFiles(prev => prev.map(item => item.id === id ? { ...item, ...patch } : item));
  };

  const uploadOne = async (item) => {
    const { id, file } = item;
    updateItem(id, { status: 'uploading', progress: 0, error: '' });
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Upload directly to external FedEx endpoint with Basic Auth
      const resp = await axios.post('https://fedexstats-production.up.railway.app/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': 'Basic ' + btoa('jy-fedex-admin:2wRN8Trg99j4kDkAuvpi')
        },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            updateItem(id, { progress: pct });
          }
        }
      });
      updateItem(id, { status: 'done', progress: 100 });
      // Persist response by appending to session list (do not override)
      try {
        const data = resp?.data ?? null;
        const displayObj = normalizeResponseToDisplayObject(data);
        setUploadResponses(prev => {
          const next = [...prev, displayObj];
          sessionStorage.setItem('fedexUploadResponses', JSON.stringify(next));
          return next;
        });
      } catch {}
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Upload failed';
      updateItem(id, { status: 'error', error: message });
    } finally {
      activeCount.current -= 1;
      pumpQueue();
    }
  };

  const pumpQueue = () => {
    while (activeCount.current < MAX_CONCURRENT_UPLOADS && uploadQueue.current.length > 0) {
      const next = uploadQueue.current.shift();
      activeCount.current += 1;
      uploadOne(next);
    }
    if (activeCount.current === 0) {
      setIsUploading(false);
      setSnackbar({ open: true, message: 'All uploads complete', severity: 'success' });
    }
  };

  const startUpload = useCallback(() => {
    if (!files.length) return;
    // Enforce single-file per upload: only first item is considered
    const first = files[0];
    if (!first) return;
    if (!(first.status === 'pending' || first.status === 'error')) return;
    setIsUploading(true);
    uploadQueue.current = [{ ...first }];
    activeCount.current = 0;
    pumpQueue();
  }, [files]);

  // Union of keys across all stored responses for table headers
  const uploadKeys = useMemo(() => {
    const set = new Set();
    (uploadResponses || []).forEach(row => {
      if (row && typeof row === 'object') {
        Object.keys(row).forEach(k => set.add(k));
      }
    });
    return Array.from(set);
  }, [uploadResponses]);

  // Handle Recal button click
  const handleRecalClick = useCallback(async () => {
    setIsRecalling(true);
    try {
      const credentials = btoa('jy-fedex-admin:2wRN8Trg99j4kDkAuvpi');
      const response = await fetch('https://fedexstats-production.up.railway.app/run-shipping-analysis?format=json', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Recal API Response:', data);
      setSnackbar({ open: true, message: 'Recal completed successfully', severity: 'success' });
    } catch (err) {
      console.error('Error calling recal API:', err);
      setSnackbar({ open: true, message: `Recal failed: ${err.message}`, severity: 'error' });
    } finally {
      setIsRecalling(false);
    }
  }, []);

  return (
    <DashboardLayout>
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '1.25rem' }}>
            FedEx Files Upload
          </Typography>
          <Button
            variant="contained"
            onClick={handleRecalClick}
            disabled={isRecalling}
            size="small"
            sx={{
              backgroundColor: '#2D3648',
              '&:hover': {
                backgroundColor: '#242b38',
              },
              '&:disabled': {
                backgroundColor: '#6b72804d',
              },
              fontSize: '0.8125rem',
              py: 0.5,
              px: 1
            }}
          >
            {isRecalling ? <CircularProgress size={18} color="inherit" /> : 'Recal'}
          </Button>
        </Box>

        <Paper
          elevation={2}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 1.5,
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.400',
            backgroundColor: isDragging ? 'rgba(25,118,210,0.04)' : 'background.paper',
            transition: 'border-color 0.2s ease',
            textAlign: 'center'
          }}
        >
          <FolderIcon sx={{ fontSize: 40, color: 'primary.main', mb: 0.75 }} />
          <Typography variant="h6" sx={{ mb: 0.75, fontSize: '1rem' }}>Drag and drop files here</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5, fontSize: '0.875rem' }}>
            or
          </Typography>
          <input ref={inputRef} type="file" style={{ display: 'none' }} onChange={handleInputChange} />
          <Button variant="contained" onClick={handleBrowseClick} disabled={isUploading} size="small" sx={{ fontSize: '0.8125rem', py: 0.5, px: 1 }}>
            Browse Files
          </Button>
          <Box sx={{ mt: 1.5, color: 'text.secondary', fontSize: '0.8125rem' }}>
            {files.length} file selected, total {humanSize}
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ mt: 1.5 }}>
          <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontSize: '1.125rem' }}>Upload Queue</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Tooltip title="Start / Resume Uploads">
                <span>
                  <Button variant="contained" onClick={startUpload} disabled={isUploading || !files.some(f => f.status === 'pending' || f.status === 'error')} size="small" sx={{ fontSize: '0.8125rem', py: 0.5, px: 1 }}>
                    Upload
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Clear completed">
                <span>
                  <IconButton onClick={() => setFiles(prev => prev.filter(f => f.status !== 'done'))} disabled={!files.some(f => f.status === 'done')} size="small" sx={{ padding: '6px' }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear all">
                <span>
                  <IconButton onClick={clearAll} disabled={isUploading || files.length === 0} size="small" sx={{ padding: '6px' }}>
                    <ClearAllIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          <List dense sx={{ maxHeight: '50vh', overflowY: 'auto', px: 1 }}>
            {files.map(item => (
              <ListItem key={item.id} divider sx={{ alignItems: 'flex-start', py: 0.75 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.875rem' }}>
                        {item.file?.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                        {(item.file?.size || 0) > 0 ? `${(item.file.size/1024/1024).toFixed(2)} MB` : ''}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box component="div" sx={{ mt: 0.75 }}>
                      <LinearProgress variant="determinate" value={item.progress} sx={{ height: 6, borderRadius: 1 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{item.progress}%</Typography>
                        <Typography variant="caption" sx={{ color: item.status === 'error' ? 'error.main' : 'text.secondary', fontSize: '0.75rem' }}>
                          {item.status === 'pending' && 'Pending'}
                          {item.status === 'uploading' && 'Uploading...'}
                          {item.status === 'done' && 'Completed'}
                          {item.status === 'error' && (item.error || 'Failed')}
                        </Typography>
                      </Box>
                    </Box>
                  }
                  primaryTypographyProps={{ component: 'div' }}
                  secondaryTypographyProps={{ component: 'div' }}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton edge="end" onClick={() => removeFile(item.id)} disabled={item.status === 'uploading'} size="small" sx={{ padding: '6px' }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
            {files.length === 0 && (
              <ListItem>
                <ListItemText primary={<Typography color="text.secondary" sx={{ fontSize: '0.8125rem' }}>No files added yet</Typography>} />
              </ListItem>
            )}
          </List>
        </Paper>

        {/* Uploads table showing appended API responses */}
        <Paper elevation={1} sx={{ mt: 1.5, p: 0 }}>
          <Box sx={{ p: 1 }}>
            <Typography variant="h6" sx={{ mb: 0.5, fontSize: '1.125rem' }}>Uploads</Typography>
            {(!uploadResponses || uploadResponses.length === 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>No upload responses yet. Upload a file to see details.</Typography>
            )}
          </Box>
          {(uploadResponses && uploadResponses.length > 0) && (
            <Box sx={{ px: 1, pb: 1, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5', height: '36px' }}>
                    {uploadKeys.map((k) => (
                      <th key={k} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: 'none', fontWeight: 600, fontSize: '0.875rem', lineHeight: 1 }}>
                        {k}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {uploadResponses.map((row, idx) => (
                    <tr key={idx} style={{ height: '48px', borderBottom: 'none' }}>
                      {uploadKeys.map((k) => {
                        const v = row?.[k];
                        const text = v !== null && typeof v === 'object' ? JSON.stringify(v) : (v === undefined ? '-' : String(v));
                        return (
                          <td key={k} style={{ padding: '8px', borderBottom: 'none', fontSize: '0.8125rem' }}>{text}</td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default FedexFiles;


